import React, { useEffect } from 'react';

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy - SongToSong";
    return () => {
      document.title = "SongToSong";
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#1a1a1a] font-sans selection:bg-blue-100">
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 tracking-tight">Privacy Policy for SongToSong</h1>
        <div className="text-sm text-gray-500 mb-10">
          <p><strong>Effective Date:</strong> April 3, 2026</p>
          <p><strong>Last Updated:</strong> April 3, 2026</p>
        </div>

        <p className="mb-8 leading-relaxed">
          SongToSong ("we", "us", "our") operates <a href="https://songtosong.app" className="text-blue-600 hover:underline">https://songtosong.app</a> ("the Service"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our Service.
        </p>

        <p className="mb-12 leading-relaxed">
          By using the Service, you agree to the collection and use of information in accordance with this policy.
        </p>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">1. Information We Collect</h2>
          <p className="mb-6">We collect the following types of information:</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold mb-2">IP address</h3>
              <p className="text-gray-700">Automatically collected on server requests. Personal data under GDPR (recital 30).</p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">Device information</h3>
              <p className="text-gray-700">OS, browser type, screen resolution. May constitute fingerprinting data.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">Usage & analytics data</h3>
              <p className="text-gray-700">Pages visited, features used, session duration. Behavioral data; requires basis under GDPR.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">Cookies & trackers</h3>
              <p className="text-gray-700">Cookies, localStorage, session IDs. Triggers ePrivacy Directive requirements.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">2. How We Use Your Information</h2>
          <p className="mb-4">We use the collected information for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>To provide and maintain our Service</li>
            <li>To notify you about changes to our Service</li>
            <li>To provide customer support</li>
            <li>To gather analysis or valuable information so that we can improve our Service</li>
            <li>To monitor the usage of our Service</li>
            <li>To detect, prevent, and address technical issues</li>
            <li>To manage cookies and similar tracking technologies</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">3. Third-Party Service Providers & APIs</h2>
          <p className="mb-6 leading-relaxed">
            We may employ third-party companies and individuals to facilitate our Service, provide the Service on our behalf, perform Service-related tasks, or assist us in analyzing how our Service is used.
          </p>
          
          <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 mb-8">
            <h3 className="text-lg font-bold mb-2 text-blue-900">Spotify API Disclaimer</h3>
            <p className="text-blue-800 text-sm leading-relaxed">
              SongToSong uses the Spotify Web API to provide artist and track information. This application is not an official Spotify product and is not affiliated with, endorsed by, or sponsored by Spotify. All artist names, track titles, and album artwork are the property of their respective owners and are provided via the Spotify API for informational and entertainment purposes only.
            </p>
          </div>

          <p className="mb-8 leading-relaxed">
            These third parties have access to your personal information only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
          </p>

          <div className="space-y-8">
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <h3 className="text-lg font-bold mb-2">Simple Analytics</h3>
              <p className="text-sm text-gray-600 mb-3"><strong>Category:</strong> Analytics</p>
              <p className="text-gray-700 mb-3">We use Simple Analytics for privacy-first website analytics. Simple Analytics does not use cookies and does not collect personal data. We use this to track game completions, challenge difficulty, search queries (to improve artist matching), and feature usage.</p>
              <a href="https://simpleanalytics.com/privacy" className="text-blue-600 text-sm hover:underline">Simple Analytics Privacy Policy</a>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <h3 className="text-lg font-bold mb-2">Umami Analytics</h3>
              <p className="text-sm text-gray-600 mb-3"><strong>Category:</strong> Analytics</p>
              <p className="text-gray-700 mb-3">We use Umami Analytics for privacy-focused, cookieless website analytics. Umami does not collect any personally identifiable information and anonymizes all data. We use this to understand user behavior, track search queries, and improve the game experience.</p>
              <a href="https://umami.is/privacy" className="text-blue-600 text-sm hover:underline">Umami Privacy Policy</a>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <h3 className="text-lg font-bold mb-2">Sentry</h3>
              <p className="text-sm text-gray-600 mb-3"><strong>Category:</strong> Error Tracking</p>
              <p className="text-gray-700 mb-3">We use Sentry for error tracking and monitoring. Sentry may receive: Error stack traces, browser info, user context, IP address.</p>
              <a href="https://sentry.io/privacy/" className="text-blue-600 text-sm hover:underline">Sentry Privacy Policy</a>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <h3 className="text-lg font-bold mb-2">Google Cloud Platform</h3>
              <p className="text-sm text-gray-600 mb-3"><strong>Category:</strong> Cloud Infrastructure</p>
              <p className="text-gray-700 mb-3">We use Google Cloud Platform for hosting and infrastructure. Google Cloud Platform may receive: Server logs, IP addresses, application data.</p>
              <a href="https://cloud.google.com/terms/cloud-privacy-notice" className="text-blue-600 text-sm hover:underline">Google Cloud Platform Privacy Policy</a>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <h3 className="text-lg font-bold mb-2">Cloudflare</h3>
              <p className="text-sm text-gray-600 mb-3"><strong>Category:</strong> Cloud Infrastructure</p>
              <p className="text-gray-700 mb-3">We use Cloudflare for hosting and infrastructure. Cloudflare may receive: IP addresses, HTTP request data, DNS queries.</p>
              <a href="https://www.cloudflare.com/privacypolicy/" className="text-blue-600 text-sm hover:underline">Cloudflare Privacy Policy</a>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <h3 className="text-lg font-bold mb-2">DigitalOcean</h3>
              <p className="text-gray-700">We use DigitalOcean as a third-party service provider. User data as described in their privacy policy.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">4. Cookies and Tracking Technologies</h2>
          <p className="mb-6 leading-relaxed">
            We use cookies and similar tracking technologies to track activity on our Service and hold certain information.
          </p>
          <p className="font-bold mb-4">Types of cookies we use:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
            <li><strong>Strictly Necessary Cookies:</strong> Required for the Service to function. Cannot be disabled.</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our Service.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed">
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">5. Your Rights Under GDPR</h2>
          <p className="mb-6 leading-relaxed">
            If you are a resident of the European Economic Area (EEA) or the United Kingdom, you have certain data protection rights under the General Data Protection Regulation (GDPR).
          </p>
          <p className="mb-6 leading-relaxed">
            SongToSong aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your personal data.
          </p>
          <p className="font-bold mb-4">You have the following rights:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-8">
            <li><strong>Right of Access:</strong> You have the right to request copies of your personal data.</li>
            <li><strong>Right to Rectification:</strong> You have the right to request correction of inaccurate personal data.</li>
            <li><strong>Right to Erasure:</strong> You have the right to request deletion of your personal data under certain conditions.</li>
            <li><strong>Right to Restrict Processing:</strong> You have the right to request restriction of processing under certain conditions.</li>
            <li><strong>Right to Data Portability:</strong> You have the right to request transfer of your data to another organization or directly to you.</li>
            <li><strong>Right to Object:</strong> You have the right to object to processing of your personal data.</li>
            <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, you have the right to withdraw consent at any time.</li>
          </ul>
          <p className="mb-6">To exercise any of these rights, please contact us at <a href="mailto:contact@songtosong.com" className="text-blue-600 hover:underline">contact@songtosong.com</a>.</p>
          <p className="mb-6 leading-relaxed">
            <strong>Legal Basis for Processing:</strong> We process your personal data based on one or more of the following legal bases: your consent, performance of a contract, compliance with legal obligations, or our legitimate interests.
          </p>
          <p className="text-gray-700">
            You also have the right to lodge a complaint with a Data Protection Authority about our collection and use of your personal data.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">6. Your Rights Under CCPA</h2>
          <p className="mb-6 leading-relaxed">
            If you are a California resident, the California Consumer Privacy Act (CCPA) grants you specific rights regarding your personal information.
          </p>
          <p className="font-bold mb-4">You have the following rights:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-8">
            <li><strong>Right to Know:</strong> You can request that we disclose what personal information we collect, use, and disclose about you.</li>
            <li><strong>Right to Delete:</strong> You can request the deletion of your personal information, subject to certain exceptions.</li>
            <li><strong>Right to Opt-Out of Sale:</strong> You can opt out of the "sale" of your personal information. We provide a "Do Not Sell My Personal Information" link on our website.</li>
            <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
          </ul>
          <p className="mb-6"><strong>Categories of Personal Information Collected:</strong> IP address, Device information, Usage & analytics data, Cookies & trackers</p>
          <p className="mb-6">To exercise your rights, please contact us at <a href="mailto:contact@songtosong.com" className="text-blue-600 hover:underline">contact@songtosong.com</a> or submit a verifiable consumer request.</p>
          <p className="text-gray-700">We will respond to your request within 45 days. If we need more time, we will notify you of the extension.</p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">7. Data Retention</h2>
          <p className="text-gray-700 leading-relaxed">
            We will retain your personal information as long as reasonably necessary to fulfill the purposes outlined in this policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">8. Do Not Track</h2>
          <p className="mb-6 leading-relaxed">
            Our Service honors Do Not Track (DNT) signals sent by your browser. When we detect a DNT signal, we disable non-essential tracking and analytics cookies. Please note that some third-party services embedded in our website may not honor DNT signals independently.
          </p>
          <p className="font-bold mb-4">You can enable DNT in your browser settings:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Chrome:</strong> Settings &gt; Privacy and Security &gt; Send a "Do Not Track" request</li>
            <li><strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Send websites a "Do Not Track" signal</li>
            <li><strong>Safari:</strong> DNT is enabled by default in recent versions</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">9. Data Deletion</h2>
          <p className="mb-6 leading-relaxed">
            You can request the deletion of your personal data by contacting us at <a href="mailto:contact@songtosong.com" className="text-blue-600 hover:underline">contact@songtosong.com</a>. We will process your request within 30 days and confirm once your data has been deleted.
          </p>
          <p className="text-gray-700">Please note that we may need to retain certain information for legal or legitimate business purposes.</p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">10. Changes to This Privacy Policy</h2>
          <p className="mb-6 leading-relaxed">
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top.
          </p>
          <p className="text-gray-700">You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mt-12 mb-6 pb-2 border-bottom border-gray-100">11. Contact Us</h2>
          <p className="mb-4">If you have any questions about this Privacy Policy, please contact us:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Email:</strong> <a href="mailto:contact@songtosong.com" className="text-blue-600 hover:underline">contact@songtosong.com</a></li>
            <li><strong>Website:</strong> <a href="https://songtosong.app" className="text-blue-600 hover:underline">https://songtosong.app</a></li>
          </ul>
        </section>

        <hr className="my-12 border-gray-100" />
        
        <p className="text-sm text-gray-400 italic">
          This privacy policy was generated by <a href="https://pliqo.vercel.app" className="hover:text-gray-600 underline">Pliqo</a> — legal pages for developers.
        </p>
        
        <div className="mt-12">
          <a href="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#ff0080] hover:opacity-80 transition-opacity">
            ← Back to SongToSong
          </a>
        </div>
      </div>
    </div>
  );
}
